import React from "react";
import { css } from "@emotion/css";
import { PiMusicNoteThin } from "react-icons/pi";
import { FaInstagram } from "react-icons/fa";
import type { Performer } from "../../../../api/services/events/types";
import { SectionCard } from "../../../../shared/ui/section-card/section-card";
import { SectionTitle } from "../../../../shared/ui/section-title/section-title";

export interface EventPerformerCardProps extends React.HTMLAttributes<HTMLDivElement> {
  performer: Performer;
}

const sectionHeader = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-sm)",
  marginBottom: "var(--spacing-lg)",
  position: "relative",
  paddingLeft: "var(--spacing-sm)",
});

const sectionIcon = css({
  color: "var(--pc-700)",
  minWidth: "20px",
  width: "20px",
  height: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const content = css({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "1.5rem",
  paddingLeft: "var(--spacing-md)",
  paddingRight: "var(--spacing-md)",

  "@media (max-width: 768px)": {
    paddingLeft: "var(--spacing-sm)",
    paddingRight: "var(--spacing-sm)",
  },

  "@media (max-width: 48rem)": {
    flexDirection: "column",
  },
});

const info = css({
  flex: 1,
  paddingRight: "1.5rem",
  
  "@media (max-width: 48rem)": {
    paddingRight: 0,
  },
});

const performerName = css({
  fontSize: "var(--fs-500)",
  color: "var(--pc-700)",
  marginBottom: "var(--spacing-xs)",
  fontWeight: 600,
  
  "@media (max-width: 768px)": {
    fontSize: "var(--fs-450)",
  },
  
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-400)",
  },
});

const instagramLink = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-xs)",
  color: "var(--pc-600)",
  textDecoration: "none",
  fontSize: "var(--fs-350)",
  marginBottom: "var(--spacing-sm)",
  transition: "color 0.2s ease",
  
  "&:hover": {
    color: "#E1306C",
  },
  
  "& svg": {
    color: "#E1306C",
  },
});

const theaterDescription = css({
  lineHeight: 1.6,
  color: "var(--pc-700)",
  fontSize: "var(--fs-350)",
  whiteSpace: "pre-line",
  
  "@media (max-width: 48rem)": {
    fontSize: "0.9375rem",
  },
  
  "@media (max-width: 30rem)": {
    fontSize: "0.875rem",
  },
});

const artistImageContainer = css({
  flex: 1,
  width: "100%",
  minWidth: "17.5rem",
  maxWidth: "28.125rem",
  borderRadius: "var(--spacing-md)",
  overflow: "hidden",
  
  "@media (max-width: 48rem)": {
    maxWidth: "100%",
  },
});

const artistImage = css({
  width: "100%",
  height: "auto",
  objectFit: "cover",
  borderRadius: "var(--spacing-md)",
  aspectRatio: "1/1",
});

export const EventPerformerCard: React.FC<EventPerformerCardProps> = ({
  performer,
  className,
  ...rest
}) => {
  const instagramHandle = performer.profileImage?.url ? `@${performer.name.replace(/\s+/g, '')}` : null;

  return (
    <SectionCard className={className} {...rest}>
      <div className={sectionHeader}>
        <PiMusicNoteThin className={sectionIcon} />
        <SectionTitle level={2}>Artista</SectionTitle>
      </div>
      
      <div className={content}>
        <div className={info}>
          <h3 className={performerName}>{performer.name}</h3>
          
          {instagramHandle && (
            <a
              href={`https://www.instagram.com/${instagramHandle.substring(1)}/`}
              target="_blank"
              rel="noopener noreferrer"
              className={instagramLink}
            >
              <FaInstagram size={16} />
              {instagramHandle}
            </a>
          )}
          
          <p className={theaterDescription}>{performer.description}</p>
        </div>
        
        {performer.profileImage && (
          <div className={artistImageContainer}>
            <img
              src={performer.profileImage.url}
              alt={performer.name}
              className={artistImage}
            />
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default EventPerformerCard;
